<mat-card appearance="outlined">
  <mat-card-content>
    <h2 class="section-title">Business verification</h2>
    <p class="muted">
      Recorded once for this business and shared by both Kickstart Cash and Debtors Finance —
      verifying here counts for every application and facility the business has.
    </p>

    @if (isLoading()) {
      <p class="muted">Loading…</p>
    } @else if (loadError()) {
      <div class="panel-error">
        <p>Couldn't load the verification record.</p>
        <button mat-stroked-button type="button" (click)="retryLoad()">Try again</button>
      </div>
    } @else if (state(); as s) {
      <dl class="facts">
        <div class="facts__row" [class.facts__row--empty]="!s.cipc_status">
          <dt>CIPC status</dt>
          <dd>
            {{ s.cipc_status_display | dash }}
            @if (s.cipc_verified_at) {
              <span class="muted"> · {{ s.cipc_verified_at | date: 'medium' }}</span>
            }
          </dd>
        </div>
        <!--
          FLOW-356 — the date trading history is measured from (§A1.3.1).
          Captured once from a CIPC document; a correction is a new row and the
          captured date stays on record (N8).
        -->
        <div class="facts__row" [class.facts__row--empty]="!s.cipc_registration_date">
          <dt>CIPC registration date</dt>
          <dd data-testid="cipc-registration-date">
            @if (s.cipc_registration_date) {
              {{ s.cipc_registration_date | date: 'd MMM y' }}
            } @else {
              Not on file — every Kickstart assessment refers until it is captured
            }
            @if (s.cipc_registration_records.length) {
              <ul class="corrections" data-testid="cipc-registration-records">
                @for (r of s.cipc_registration_records; track r.short_id) {
                  <li>
                    @if (r.previous_date) {
                      Corrected from {{ r.previous_date | date: 'd MMM y' }} to
                      {{ r.registration_date | date: 'd MMM y' }} · {{ r.reason }}
                    } @else {
                      Captured as {{ r.registration_date | date: 'd MMM y' }}
                    }
                    <span class="muted">
                      · {{ r.recorded_by_name }} · {{ r.recorded_at | date: 'medium' }}</span
                    >
                    <button mat-button type="button" (click)="openCipcFile(r.file_id)">
                      Open {{ r.file_name }}
                    </button>
                  </li>
                }
              </ul>
            }
          </dd>
        </div>
        <!--
          FLOW-342 — the legal form as now recorded, and every correction made
          at the CIPC check. Append-only: the captured form stays readable as
          the oldest correction's "from".
        -->
        <div class="facts__row">
          <dt>Legal form</dt>
          <dd>
            {{ s.type_display }}
            @if (s.type_corrections[0]; as latest) {
              <span class="muted"> · corrected {{ latest.recorded_at | date: 'mediumDate' }}</span>
            }
            @if (s.type_corrections.length) {
              <ul class="corrections" data-testid="type-corrections">
                @for (c of s.type_corrections; track c.short_id) {
                  <li>
                    {{ c.from_type_display }} → {{ c.to_type_display }} · {{ c.reason }}
                    <span class="muted">
                      · {{ c.recorded_by_name || 'Unknown' }} ·
                      {{ c.recorded_at | date: 'medium' }}</span
                    >
                  </li>
                }
              </ul>
            }
          </dd>
        </div>
        <div class="facts__row" [class.facts__row--empty]="!s.kyc_verified">
          <dt>KYC / identity</dt>
          <dd>
            {{ s.kyc_verified ? 'Verified' : '—' }}
            @if (s.kyc_verified_at) {
              <span class="muted"> · {{ s.kyc_verified_at | date: 'medium' }}</span>
            }
          </dd>
        </div>
        <div class="facts__row" [class.facts__row--empty]="!s.sars_verified">
          <dt>SARS</dt>
          <dd>
            {{ s.sars_verified ? 'Verified' : '—' }}
            @if (s.sars_verified_at) {
              <span class="muted"> · {{ s.sars_verified_at | date: 'medium' }}</span>
            }
            @if (taxPin(); as tp) {
              @for (p of tp.pins; track p.short_id; let first = $first) {
                <p class="tax-pin" [class.muted]="!first" data-testid="tax-pin">
                  Tax Compliance Status PIN: <strong>{{ p.pin }}</strong> — sent by
                  {{ p.given_by_name }}{{ p.for_the_business ? ' for the business' : '' }} on
                  {{ p.given_at | date: 'd MMM y, HH:mm' }}
                </p>
              }
              @if (tp.pins.length) {
                <p class="tax-pin muted">Check this PIN on SARS eFiling, then record the result.</p>
              } @else if (tp.owed) {
                <p class="tax-pin muted">Waiting for the business's Tax Compliance Status PIN</p>
              }
            }
          </dd>
        </div>
        <div class="facts__row" [class.facts__row--empty]="!s.bank_account_ownership_verified">
          <dt>Bank account ownership</dt>
          <dd>
            {{ s.bank_account_ownership_verified ? 'Verified' : '—' }}
            @if (s.verified_bank_account; as acc) {
              <span class="muted">
                · {{ acc.bank_name }} ····{{ acc.account_number.slice(-4) }}</span
              >
            }
            @if (s.bank_account_verified_at) {
              <span class="muted"> · {{ s.bank_account_verified_at | date: 'medium' }}</span>
            }
          </dd>
        </div>
      </dl>

      <!--
        FLOW-356 — capture the registration date off a CIPC document (the CoR
        14.3 or a CIPC disclosure). The bureau route is not built, so Ops is the
        only source. Once on file it is corrected, never overwritten.
      -->
      @if (!s.cipc_registration_date || showCipcCorrection()) {
        <form [formGroup]="cipcForm" (ngSubmit)="recordCipcDate()" class="form">
          <h3 class="section-title">
            {{
              s.cipc_registration_date
                ? 'Correct the CIPC registration date'
                : 'CIPC registration date'
            }}
          </h3>
          <p class="muted">
            Read it off a CIPC document and attach that document. Trading history is measured from
            this date at every assessment; nothing the client typed is used.
          </p>
          <mat-form-field appearance="outline">
            <mat-label>Registration date on the CIPC document</mat-label>
            <input
              matInput
              type="date"
              formControlName="registration_date"
              data-testid="cipc-registration-date-input"
            />
          </mat-form-field>
          <label class="file-field">
            <span>The CIPC document</span>
            <input type="file" (change)="pickCipcFile($event)" data-testid="cipc-document-input" />
          </label>
          @if (s.cipc_registration_date) {
            <p class="muted">
              The date on file ({{ s.cipc_registration_date | date: 'd MMM y' }}) stays on record.
            </p>
            <mat-form-field appearance="outline">
              <mat-label>Reason for the correction</mat-label>
              <textarea matInput rows="2" formControlName="correction_reason"></textarea>
            </mat-form-field>
          }
          @if (cipcError(); as err) {
            <p class="panel-error__text" role="alert">{{ err }}</p>
          }
          <div class="form__actions">
            <button
              mat-stroked-button
              type="submit"
              [disabled]="isRecordingCipc()"
              data-testid="record-cipc-date-btn"
            >
              {{ isRecordingCipc() ? 'Recording…' : 'Record registration date' }}
            </button>
            @if (s.cipc_registration_date) {
              <button mat-button type="button" (click)="toggleCipcCorrection()">Cancel</button>
            }
          </div>
        </form>
      } @else {
        <div class="form__actions">
          <button
            mat-button
            type="button"
            (click)="toggleCipcCorrection()"
            data-testid="correct-cipc-date-btn"
          >
            Correct the registration date
          </button>
        </div>
      }

      <!--
        Bank accounts (BRE-SCH-001 3.23). Ops-only (N4): the applicant declares
        an account, Ops confirms it belongs to them (KS-OB-03). Listed here
        rather than on its own screen because the verification that names an
        account lives in this panel — separating them would make Ops hold a
        short_id in their head across two pages.
      -->
      <section class="accounts">
        <div class="accounts__head">
          <h3 class="section-title">Bank accounts</h3>
          <button
            mat-stroked-button
            type="button"
            (click)="toggleCapture()"
            data-testid="toggle-capture-account-btn"
          >
            {{ showCapture() ? 'Cancel' : 'Add account' }}
          </button>
        </div>

        @if (accountsError()) {
          <div class="panel-error">
            <p>Couldn't load the bank accounts.</p>
            <button mat-stroked-button type="button" (click)="retryAccounts()">Try again</button>
          </div>
        } @else if (accounts().length === 0 && !showCapture()) {
          <p class="muted">
            No bank accounts yet. Add the account the client declared, then confirm it belongs to
            them.
          </p>
        } @else {
          <ul class="accounts__list">
            @for (acc of accounts(); track acc.short_id) {
              <li class="accounts__item">
                <div>
                  <strong>{{ acc.bank_name }}</strong>
                  <span class="muted"> · {{ acc.branch_code }} · {{ acc.account_number }}</span>
                </div>
                <div class="muted">
                  Held by {{ acc.account_holder_name }}
                  @if (acc.holder_is_borrower === false) {
                    · <strong>third party</strong>
                  } @else if (acc.holder_is_borrower === null) {
                    · holder not confirmed
                  }
                </div>
                <div [class.muted]="!acc.ownership_verified">
                  {{ acc.ownership_verified ? 'Ownership verified' : 'Not verified' }}
                  @if (acc.ownership_verified_at) {
                    · {{ acc.ownership_verified_at | date: 'medium' }}
                  }
                </div>
              </li>
            }
          </ul>
        }

        @if (showCapture()) {
          <form [formGroup]="captureForm" (ngSubmit)="captureAccount()" class="form">
            <p class="muted">
              Adding an account records what the client declared. It does not verify it — confirm
              ownership below, naming this account.
            </p>

            <mat-form-field appearance="outline">
              <mat-label>Bank</mat-label>
              <input matInput formControlName="bank_name" data-testid="capture-bank-name" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Branch code</mat-label>
              <input matInput formControlName="branch_code" data-testid="capture-branch-code" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Account number</mat-label>
              <input
                matInput
                formControlName="account_number"
                data-testid="capture-account-number"
              />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Account holder</mat-label>
              <input
                matInput
                formControlName="account_holder_name"
                data-testid="capture-holder-name"
              />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Who holds this account?</mat-label>
              <mat-select formControlName="holder_is_borrower">
                @for (opt of holderOptions; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-select>
              <mat-hint>Leave blank if you haven't confirmed this.</mat-hint>
            </mat-form-field>

            @if (captureError(); as err) {
              <p class="panel-error__text" role="alert">{{ err }}</p>
            }

            <div class="form__actions">
              <button
                mat-stroked-button
                type="submit"
                [disabled]="isCapturing()"
                data-testid="capture-account-btn"
              >
                {{ isCapturing() ? 'Adding…' : 'Add account' }}
              </button>
            </div>
          </form>
        }
      </section>

      <form [formGroup]="form" (ngSubmit)="save()" class="form">
        <p class="muted">
          Change only what you have checked — anything you leave alone stays as it is.
        </p>

        <mat-form-field appearance="outline">
          <mat-label>CIPC status</mat-label>
          <mat-select formControlName="cipc_status">
            @for (opt of cipcOptions; track opt.value) {
              <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Legal form on the CIPC record</mat-label>
          <mat-select formControlName="legal_form" data-testid="legal-form-select">
            @for (opt of entityTypes; track opt.value) {
              <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
            }
          </mat-select>
          @if (suggestedOther(); as hint) {
            <mat-hint data-testid="legal-form-hint">
              {{ s.reg_no }} usually means {{ typeLabels[hint] }} — check the CIPC record.
            </mat-hint>
          }
        </mat-form-field>

        <!--
          Shown only when the selection differs from the type as recorded.
          Recording it corrects the business's legal form; the captured value
          stays on record (N8).
        -->
        @if (typeDiffers()) {
          <p class="muted">
            Recorded as {{ s.type_display }}. Recording this corrects the business's legal form; the
            captured value stays on record.
          </p>
          <mat-form-field appearance="outline">
            <mat-label>Registration number as printed (optional)</mat-label>
            <input matInput formControlName="reg_no_as_printed" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Reason for the correction</mat-label>
            <textarea
              matInput
              formControlName="type_correction_reason"
              rows="2"
              data-testid="type-correction-reason"
            ></textarea>
          </mat-form-field>
        }

        <div class="checks">
          <mat-checkbox formControlName="kyc_verified">KYC / identity verified</mat-checkbox>
          <mat-checkbox formControlName="sars_verified">SARS verified</mat-checkbox>
          <mat-checkbox formControlName="bank_account_ownership_verified">
            Bank account ownership verified
          </mat-checkbox>
        </div>

        <!--
          Shown only when the box is ticked. A verification has to name the
          account it confirms (BRE-SCH-001 v1.16 §3), and only a positive one
          does — "not verified" is a statement about the business.
        -->
        @if (form.controls.bank_account_ownership_verified.value) {
          <mat-form-field appearance="outline">
            <mat-label>Which account did you verify?</mat-label>
            <mat-select formControlName="bank_account" data-testid="verified-account-select">
              @for (acc of accounts(); track acc.short_id) {
                <mat-option [value]="acc.short_id">
                  {{ acc.bank_name }} · {{ acc.account_number }} · {{ acc.account_holder_name }}
                </mat-option>
              }
            </mat-select>
            @if (accounts().length === 0) {
              <mat-hint>Add the account above first.</mat-hint>
            }
          </mat-form-field>
        }

        @if (serverError(); as err) {
          <p class="panel-error__text" role="alert">{{ err }}</p>
        }

        <div class="form__actions">
          <button
            mat-stroked-button
            type="submit"
            [disabled]="isSaving()"
            data-testid="record-verification-btn"
          >
            {{ isSaving() ? 'Recording…' : 'Record verification' }}
          </button>
        </div>
      </form>
    }
  </mat-card-content>
</mat-card>
